// ---------------------------------------------------------------------------
// behaviorIncidentsCsv — one CSV writer that every "Export" surface on the
// Behavioral Incident Logging page uses. Historically this logic lived
// inside IncidentReportTable, but the top-of-page "Export" button in
// pages/behavioral-incident-logging/index.jsx was rendered without an
// onClick, so users clicking the obvious button got nothing. Extracting
// the helper keeps the two entry points from drifting.
//
// Public API:
//
//   buildIncidentsCsv(rows)     → String  (RFC 4180-ish CSV text)
//   downloadIncidentsCsv({ rows, studentName?, filename? })
//     → triggers a browser download; returns { rowCount, filename }.
//
// Both accept incident objects in the same normalized shape that
// IncidentsContext hands out (camelCase, `occurredAt`, `behaviorType`,
// `intensity`, `durationSeconds`, `hypothesizedFunction`, etc.). A row
// may optionally carry `_reporterName` — a pre-resolved display name for
// the "Reporter" column. Callers that don't want to look up profile
// display names can pass a rows array with `_reporterName` omitted and
// the column will fall back to the raw `reportedBy` id (or blank).
// ---------------------------------------------------------------------------

import { FUNCTION_BY_VALUE } from './pbisConstants';
import {
  ANTECEDENT_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  INTERVENTION_OPTIONS,
  LOCATION_OPTIONS,
  SETTING_ACTIVITIES,
  STUDENT_REACTION_OPTIONS,
} from './pbisConstants';

const labelLookup = (options) =>
  Object.fromEntries((options || []).map((o) => [o.value, o.label]));

const LOOKUPS = {
  antecedent: labelLookup(ANTECEDENT_OPTIONS),
  behavior: labelLookup(BEHAVIOR_TYPE_OPTIONS),
  consequence: labelLookup(CONSEQUENCE_OPTIONS),
  location: labelLookup(LOCATION_OPTIONS),
  setting: labelLookup(SETTING_ACTIVITIES),
  studentReaction: labelLookup(STUDENT_REACTION_OPTIONS),
  intervention: labelLookup(INTERVENTION_OPTIONS),
};

const humanize = (kind, value) => {
  if (!value) return '';
  return LOOKUPS[kind]?.[value] || String(value).replace(/-/g, ' ');
};

const formatDate = (row) => {
  const raw = row?.occurredAt || row?.timestamp;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US');
};

const formatTime = (row) => {
  const raw = row?.occurredAt || row?.timestamp;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// CSV escaping per RFC 4180 — wrap in quotes and double any inner quote
// only when the field contains a comma, quote, or newline.
export const escapeCsv = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const CSV_HEADER = [
  'Date',
  'Time',
  'Student',
  'Behavior type',
  'Behavior description',
  'Location',
  'Setting / activity',
  'Antecedent',
  'Antecedent notes',
  'Consequence',
  'Student reaction',
  'Function',
  'Intensity (1-5)',
  'Duration (mm:ss)',
  'Intervention',
  'Staff involved',
  'Follow-up needed',
  'Notes',
  'Reporter',
];

const rowToCsvFields = (row) => {
  const durationSeconds = row?.durationSeconds;
  const durationCsv =
    durationSeconds == null
      ? row?.duration || ''
      : `${Math.floor(durationSeconds / 60)}:${String(
          durationSeconds % 60,
        ).padStart(2, '0')}`;

  return [
    formatDate(row),
    formatTime(row),
    row?._studentName || row?.studentName || row?.studentId || '',
    humanize('behavior', row?.behaviorType),
    row?.behaviorDescription || '',
    humanize('location', row?.location),
    humanize('setting', row?.settingActivity),
    humanize('antecedent', row?.antecedent),
    row?.antecedentNotes || '',
    humanize('consequence', row?.consequence),
    humanize('studentReaction', row?.studentReaction),
    FUNCTION_BY_VALUE[row?.hypothesizedFunction]?.label ||
      row?.hypothesizedFunction ||
      '',
    row?.intensity ?? '',
    durationCsv,
    humanize('intervention', row?.interventionUsed),
    Array.isArray(row?.staffInvolved)
      ? row.staffInvolved.join('; ')
      : row?.staffInvolved || '',
    row?.followUpRequired ? 'Yes' : 'No',
    row?.notes || '',
    row?._reporterName || row?.reportedBy || '',
  ];
};

export const buildIncidentsCsv = (rows) => {
  const lines = [CSV_HEADER.map(escapeCsv).join(',')];
  for (const r of rows || []) {
    lines.push(rowToCsvFields(r).map(escapeCsv).join(','));
  }
  return lines.join('\n');
};

// Trigger a browser download of the incidents CSV.
//
// Accepts either:
//   downloadIncidentsCsv({ rows, studentName, filename })
//   downloadIncidentsCsv(rows, studentName)   // backwards-compat
//
// Returns { rowCount, filename } so callers can toast "Exported N incidents".
// If `rows` is empty, still writes a header-only file so a user who clicks
// Export before any incidents exist gets a real file with column labels
// (not a silent no-op that looks broken).
export const downloadIncidentsCsv = (a, b) => {
  const opts =
    a && typeof a === 'object' && !Array.isArray(a)
      ? a
      : { rows: a, studentName: b };
  const rows = opts.rows || [];
  const studentName = opts.studentName;
  const providedName = opts.filename;

  const csv = buildIncidentsCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const safeName = studentName
    ? studentName.replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()
    : 'all-students';
  const stamp = new Date().toISOString().slice(0, 10);
  const filename =
    providedName || `behavior-incidents-${safeName}-${stamp}.csv`;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { rowCount: rows.length, filename };
};
